import { useState, useRef } from 'react';
import { Camera, Upload, Loader2, Sparkles, AlertTriangle, Heart, Sun, Moon, Shield, Droplets, X, Eye, Gem, CheckCircle2, Save, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CrystalIdentification {
  identified: boolean;
  confidence?: string;
  name?: string;
  alternativeNames?: string[];
  colour?: string;
  crystalSystem?: string;
  hardness?: string;
  chakras?: string[];
  element?: string;
  zodiacSigns?: string[];
  healingProperties?: string[];
  emotionalBenefits?: string[];
  physicalAssociations?: string[];
  careInstructions?: string;
  cleansingMethods?: string[];
  chargingMethods?: string[];
  warnings?: string[];
  interestingFacts?: string[];
  affirmation?: string;
  notIdentifiedReason?: string;
}

const authenticityTips = [
  {
    title: 'Temperature Test',
    description: 'Real crystals feel cool to the touch and take time to warm up. Glass or plastic will warm quickly.',
    icon: Gem
  },
  {
    title: 'Weight & Density',
    description: 'Genuine crystals feel heavier than they look. Fake crystals often feel lighter or hollow.',
    icon: Gem
  },
  {
    title: 'Natural Imperfections',
    description: 'Real crystals have natural inclusions, slight colour variations, and imperfections. Too-perfect specimens may be synthetic.',
    icon: Eye
  },
  {
    title: 'Hardness Test',
    description: 'Most genuine crystals cannot be scratched with a fingernail. Quartz (7 on Mohs scale) scratches glass.',
    icon: Shield
  },
  {
    title: 'Air Bubbles',
    description: 'Look for tiny air bubbles inside - these indicate glass, not crystal. Use a magnifying glass if needed.',
    icon: Eye
  },
  {
    title: 'Price Point',
    description: 'If a rare crystal is priced very cheaply, it may be dyed, heat-treated, or synthetic. Research fair market prices.',
    icon: CheckCircle2
  }
];

export function CrystalIdentifier() {
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [result, setResult] = useState<CrystalIdentification | null>(null);
  const [showTips, setShowTips] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const saveToCollection = async () => {
    if (!result?.identified || !result.name) return;
    
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in to save crystals');
        return;
      }

      const { error } = await (supabase.from('user_crystal_collection') as any).insert({
        user_id: user.id,
        crystal_name: result.name,
        image_url: imagePreview,
        identification_data: result,
        is_owned: true
      });

      if (error) throw error;
      
      setIsSaved(true);
      toast.success(`${result.name} saved to your collection!`);
    } catch (error: any) {
      console.error('Save error:', error);
      toast.error('Failed to save crystal');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Convert to base64 for API
    const base64Reader = new FileReader();
    base64Reader.onload = async (e) => {
      const base64 = (e.target?.result as string).split(',')[1];
      await identifyCrystal(base64);
    };
    base64Reader.readAsDataURL(file);
  };

  const identifyCrystal = async (imageBase64: string) => {
    setIsLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('identify-crystal', {
        body: { imageBase64 }
      });

      if (error) throw error;

      if (data.error) {
        toast.error(data.error);
        return;
      }

      setResult(data);
      
      if (data.identified) {
        toast.success(`Identified: ${data.name}!`);
      } else {
        toast.info('Could not identify this crystal with confidence');
      }
    } catch (error: any) {
      console.error('Crystal identification error:', error);
      toast.error(error.message || 'Failed to identify crystal');
    } finally {
      setIsLoading(false);
    }
  };

  const clearResult = () => {
    setResult(null);
    setImagePreview(null);
    setIsSaved(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  const openGallery = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="p-3 bg-secondary/20 rounded-lg border border-secondary/30 text-sm text-foreground/80">
        <p>
          <strong>Please note:</strong> AI identification is based on visual analysis and should be used as guidance only. 
          For valuable specimens or important decisions, please consult a qualified gemologist.
        </p>
      </div>

      {/* Authenticity Tips */}
      <Collapsible open={showTips} onOpenChange={setShowTips}>
        <Card className="bg-muted/30 border-muted/50">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/20 transition-colors">
              <CardTitle className="flex items-center justify-between text-base">
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-accent" />
                  How to Spot Authentic Crystals
                </div>
                <Badge variant="outline" className="text-xs">
                  {showTips ? 'Hide' : 'Show'}
                </Badge>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {authenticityTips.map((tip, index) => {
                  const IconComponent = tip.icon;
                  return (
                    <div key={index} className="p-3 bg-background/50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                          <IconComponent className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium text-sm text-foreground">{tip.title}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{tip.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Upload Card */}
      <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Crystal Identifier
          </CardTitle>
          <CardDescription>
            Take a photo or upload an image of your crystal. For best results, ensure good lighting and capture the crystal from multiple angles if needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />

          {!imagePreview ? (
            <div className="space-y-4">
              {/* Photo Tips */}
              <div className="p-4 bg-background/50 rounded-lg border border-border/30">
                <h4 className="font-medium text-sm text-foreground mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent" />
                  Tips for Best Results
                </h4>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  <li>• Use natural daylight for most accurate colours</li>
                  <li>• Avoid direct flash which can wash out colours</li>
                  <li>• Include any distinctive markings or inclusions</li>
                  <li>• If possible, show both polished and raw surfaces</li>
                  <li>• A plain background helps the AI focus on the crystal</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={openCamera}
                  className="flex items-center justify-center gap-2 h-24"
                  variant="outline"
                >
                  <div className="text-center">
                    <Camera className="h-8 w-8 mx-auto mb-2 text-primary" />
                    <span className="text-sm font-medium">Take Photo</span>
                  </div>
                </Button>
                <Button 
                  onClick={openGallery}
                  className="flex items-center justify-center gap-2 h-24"
                  variant="outline"
                >
                  <div className="text-center">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-primary" />
                    <span className="text-sm font-medium">Upload Image</span>
                  </div>
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <img 
                src={imagePreview} 
                alt="Crystal preview" 
                className="w-full max-h-64 object-contain rounded-xl"
              />
              <Button
                variant="outline"
                size="icon"
                className="absolute top-2 right-2 bg-background/80"
                onClick={clearResult}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center gap-3 py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-foreground/70">Analysing your crystal...</span>
            </div>
          )}

          {imagePreview && !isLoading && !result && (
            <div className="flex gap-3">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={openCamera}
              >
                <Camera className="h-4 w-4 mr-2" />
                Retake
              </Button>
              <Button 
                variant="outline"
                className="flex-1"
                onClick={openGallery}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Different
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Result Card */}
      {result && (
        <Card className="bg-card/80 backdrop-blur-sm border-accent/30">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                {result.identified ? (
                  <>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      <Sparkles className="h-6 w-6 text-primary" />
                      {result.name}
                    </CardTitle>
                    {result.alternativeNames && result.alternativeNames.length > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Also known as: {result.alternativeNames.join(', ')}
                      </p>
                    )}
                  </>
                ) : (
                  <CardTitle className="text-xl text-muted-foreground">
                    Crystal Not Identified
                  </CardTitle>
                )}
              </div>
              {result.confidence && (
                <Badge 
                  variant="secondary"
                  className={
                    result.confidence === 'high' 
                      ? 'bg-accent/20 text-accent border-accent/30' 
                      : result.confidence === 'medium'
                      ? 'bg-primary/20 text-primary border-primary/30'
                      : 'bg-secondary/20 text-secondary border-secondary/30'
                  }
                >
                  {result.confidence} confidence
                </Badge>
              )}
            </div>
          </CardHeader>

          {result.identified && (
            <CardContent className="px-3 sm:px-6">
              <ScrollArea className="h-[500px] pr-2 sm:pr-4">
                <div className="space-y-5">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {result.colour && (
                      <div className="p-3 bg-muted/30 rounded-lg overflow-hidden">
                        <p className="text-xs text-muted-foreground mb-1">Colour</p>
                        <p className="text-sm font-medium break-words overflow-wrap-anywhere">{result.colour}</p>
                      </div>
                    )}
                    {result.element && (
                      <div className="p-3 bg-muted/30 rounded-lg overflow-hidden">
                        <p className="text-xs text-muted-foreground mb-1">Element</p>
                        <p className="text-sm font-medium break-words overflow-wrap-anywhere">{result.element}</p>
                      </div>
                    )}
                    {result.hardness && (
                      <div className="p-3 bg-muted/30 rounded-lg overflow-hidden">
                        <p className="text-xs text-muted-foreground mb-1">Hardness (Mohs)</p>
                        <p className="text-sm font-medium break-words overflow-wrap-anywhere">{result.hardness}</p>
                      </div>
                    )}
                    {result.crystalSystem && (
                      <div className="p-3 bg-muted/30 rounded-lg overflow-hidden">
                        <p className="text-xs text-muted-foreground mb-1">Crystal System</p>
                        <p className="text-sm font-medium break-words overflow-wrap-anywhere">{result.crystalSystem}</p>
                      </div>
                    )}
                  </div>

                  {/* Warnings */}
                  {result.warnings && result.warnings.length > 0 && (
                    <div className="p-3 sm:p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                        <h4 className="font-medium text-amber-500">Safety Warnings</h4>
                      </div>
                      <ul className="space-y-1.5">
                        {result.warnings.map((warning, i) => (
                          <li key={i} className="text-sm text-amber-400 break-words overflow-wrap-anywhere pr-2">• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Chakras */}
                  {result.chakras && result.chakras.length > 0 && (
                    <div>
                      <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                        <Sun className="h-4 w-4 text-primary flex-shrink-0" />
                        Associated Chakras
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {result.chakras.map((chakra, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{chakra}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Zodiac Signs */}
                  {result.zodiacSigns && result.zodiacSigns.length > 0 && (
                    <div>
                      <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                        <Moon className="h-4 w-4 text-secondary flex-shrink-0" />
                        Zodiac Signs
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {result.zodiacSigns.map((sign, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{sign}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Healing Properties */}
                  {result.healingProperties && result.healingProperties.length > 0 && (
                    <div>
                      <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-accent flex-shrink-0" />
                        Healing Properties
                      </h4>
                      <ul className="space-y-1.5">
                        {result.healingProperties.map((prop, i) => (
                          <li key={i} className="text-sm text-foreground/70 break-words pr-2">• {prop}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Emotional Benefits */}
                  {result.emotionalBenefits && result.emotionalBenefits.length > 0 && (
                    <div>
                      <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                        <Heart className="h-4 w-4 text-primary flex-shrink-0" />
                        Emotional Benefits
                      </h4>
                      <ul className="space-y-1.5">
                        {result.emotionalBenefits.map((benefit, i) => (
                          <li key={i} className="text-sm text-foreground/70 break-words pr-2">• {benefit}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Care Instructions */}
                  {result.careInstructions && (
                    <div className="p-3 sm:p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                        Care Instructions
                      </h4>
                      <p className="text-sm text-foreground/70 break-words">{result.careInstructions}</p>
                    </div>
                  )}

                  {/* Cleansing & Charging */}
                  <div className="grid grid-cols-1 gap-3">
                    {result.cleansingMethods && result.cleansingMethods.length > 0 && (
                      <div className="p-3 sm:p-4 bg-secondary/10 rounded-lg">
                        <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                          <Droplets className="h-4 w-4 text-secondary flex-shrink-0" />
                          Cleansing Methods
                        </h4>
                        <ul className="space-y-1.5">
                          {result.cleansingMethods.map((method, i) => (
                            <li key={i} className="text-sm text-foreground/70 break-words pr-2">• {method}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {result.chargingMethods && result.chargingMethods.length > 0 && (
                      <div className="p-3 sm:p-4 bg-primary/10 rounded-lg">
                        <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                          <Sun className="h-4 w-4 text-primary flex-shrink-0" />
                          Charging Methods
                        </h4>
                        <ul className="space-y-1.5">
                          {result.chargingMethods.map((method, i) => (
                            <li key={i} className="text-sm text-foreground/70 break-words pr-2">• {method}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Interesting Facts */}
                  {result.interestingFacts && result.interestingFacts.length > 0 && (
                    <div className="p-3 sm:p-4 bg-accent/10 rounded-lg">
                      <h4 className="font-medium text-foreground mb-2">Interesting Facts</h4>
                      <ul className="space-y-2">
                        {result.interestingFacts.map((fact, i) => (
                          <li key={i} className="text-sm text-foreground/70 break-words pr-2">💎 {fact}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Affirmation */}
                  {result.affirmation && (
                    <div className="p-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground mb-2">Affirmation for {result.name}</p>
                      <p className="text-foreground italic">"{result.affirmation}"</p>
                    </div>
                  )}

                  {/* Save to Collection */}
                  <div className="pt-4 border-t border-border/30">
                    <Button 
                      onClick={saveToCollection}
                      disabled={isSaving || isSaved}
                      className="w-full mb-3"
                      variant={isSaved ? "secondary" : "default"}
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : isSaved ? (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {isSaved ? 'Saved to Collection' : 'Save to My Collection'}
                    </Button>
                    
                    <div className="flex gap-3">
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={openCamera}
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Scan Another
                      </Button>
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={openGallery}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Another
                      </Button>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          )}

          {!result.identified && result.notIdentifiedReason && (
            <CardContent>
              <p className="text-muted-foreground">{result.notIdentifiedReason}</p>
              <div className="mt-4 flex gap-3">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={openCamera}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Take Photo
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={openGallery}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Image
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}